import type { StatementConfigDTO } from '@/app/(dashboard)/master/statement/apis/use-get-statement-configs';

export const STATEMENT_CONFIG_CHANGED = 'STATEMENT_CONFIG_CHANGED';

export interface StatementConflict {
  /** The row as a colleague left it; null when an older backend omitted it. */
  current: StatementConfigDTO | null;
  message: string | null;
}

/**
 * Reads a 409 from PUT /statement-configs/me or PATCH /statement-configs/:id.
 * Any other error returns null. A 409 whose code is missing still counts, so
 * an older response shape falls back to the refetch path.
 */
export const parseStatementConflict = (error: unknown): StatementConflict | null => {
  const maybe = error as {
    response?: {
      status?: number;
      data?: { message?: string; error?: { code?: string; message?: string; current?: unknown } };
    };
  };
  if (maybe?.response?.status !== 409) return null;
  const body = maybe.response.data;
  const code = body?.error?.code;
  if (code && code !== STATEMENT_CONFIG_CHANGED) return null;
  const current = body?.error?.current;
  const isRow = !!current && typeof current === 'object' && typeof (current as { id?: unknown }).id === 'number';
  return {
    current: isRow ? (current as StatementConfigDTO) : null,
    message: body?.error?.message || body?.message || null,
  };
};

/**
 * The `expectedUpdatedAt` to send: the `updatedAt` of the row the form was
 * opened with. No row (creating the first Utama through /me) or a row that was
 * never updated sends null.
 */
export const expectedUpdatedAtFor = (row: Pick<StatementConfigDTO, 'updatedAt'> | null | undefined): string | null =>
  row?.updatedAt ? row.updatedAt : null;

/**
 * The row to show when a 409 came without `error.current`: the same id when
 * editing a row, otherwise the PPJK's Utama (first active row as a fallback).
 */
export const pickConflictFallback = (
  rows: readonly StatementConfigDTO[] | null | undefined,
  id?: number | null
): StatementConfigDTO | null => {
  if (!rows?.length) return null;
  if (id != null) {
    const same = rows.find((row) => row.id === id);
    if (same) return same;
  }
  const active = rows.filter((row) => row.isActive !== false);
  return active.find((row) => row.isDefault) ?? active[0] ?? null;
};
